// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { APP_CURRENCY, APP_LOCALE, DEFAULT_SPENDING_CAP_GBP } from "./constants.js";
import { t as ct } from "./content.js";
import { todayStr, weekStartOf } from "./dates.js";

// Request types that count as a child "earning" tokens, for spending-cap purposes — everything
// else (redemptions, transfers) is either a spend or a balance move, not new earnings, so it
// never counts toward or against the weekly cap.
export const EARN_REQUEST_TYPES = ["earn", "habit_reward", "learn_earn_reward"];

/* ----------------------------- number formatting -----------------------------
 * All number display goes through Intl.NumberFormat, driven by APP_LOCALE/APP_CURRENCY in
 * constants.js — the single point a future locale change touches. Formatters are created
 * once at module load (they're expensive to construct, cheap to reuse). For en-GB the
 * output is byte-identical to the old `£${x.toFixed(2)}` for values under 1,000; at and
 * above 1,000 it gains the thousands separator (£1,234.56 / 12,500) that raw
 * interpolation never had.
 * ---------------------------------------------------------------------------- */

const CURRENCY_FMT = new Intl.NumberFormat(APP_LOCALE, { style: "currency", currency: APP_CURRENCY, minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NUMBER_FMT = new Intl.NumberFormat(APP_LOCALE);

// Formats a plain count (token balances, Oinks, goal targets) with locale grouping — 12500
// renders as "12,500" in en-GB. Non-numeric input falls back to 0 rather than "NaN".
export function fmtNumber(n) {
  return NUMBER_FMT.format(typeof n === "number" && isFinite(n) ? n : 0);
}

// Converts a token amount to a £ display string using the household's exchange rate (tokens per £1).

export function fmtGBP(tokens, exchangeRate) {
  const rate = exchangeRate > 0 ? exchangeRate : 10;
  return CURRENCY_FMT.format(tokens / rate);
}

// Bank balances are stored in pence (integer) to avoid floating-point drift.

export function fmtPence(pence) {
  return CURRENCY_FMT.format((pence || 0) / 100);
}

// LB-324: the wizard money step's cap line. The old line ("£5 of pocket money a week ≈ 50
// tokens") followed the exchange rate but ignored the cap the parent had just chosen. This
// describes THEIR cap, in the unit the cap control uses (pounds), and is recomputed from
// both controls. Pounds are formatted the way the step's summary line formats them.
export function weeklyCapLine(exchangeRate, capGbp) {
  const rate = exchangeRate > 0 ? exchangeRate : 10;
  const gbp = capGbp > 0 ? capGbp : DEFAULT_SPENDING_CAP_GBP;
  const pounds = fmtPence(Math.round(gbp * 100)).replace(/^[^\d]+/, "");
  return ct("wizard.money.cap_line", "A weekly cap of £{pounds} is about {tokens} tokens a week.", { pounds, tokens: Math.round(gbp * rate) });
}

export function tokensToPence(tokens, exchangeRate) {
  const rate = exchangeRate > 0 ? exchangeRate : 10;
  return Math.round((tokens / rate) * 100);
}

export function penceToTokens(pence, exchangeRate) {
  const rate = exchangeRate > 0 ? exchangeRate : 10;
  return Math.round(((pence || 0) / 100) * rate);
}

/* --------------------------- exchange-rate rescaling -------------------------
 * Every token-denominated *reward* default in the app (habit rewards, one-off
 * activities, spelling/vocab/TT/maths pass & full rewards) is tuned assuming the
 * household's exchange rate. If a parent later changes the rate, those token
 * amounts would silently gain or lose real £ value unless rescaled alongside it —
 * see TokenSetupFields.saveExchangeRate (SettingsTab.jsx), the only place this
 * fires, with ratio = newRate / oldRate. A ratio of 1 (rate unchanged) is a no-op
 * skipped by the caller before this is even called.
 * Deliberately NOT touched here: experienceRewards (parent-set redemption prices —
 * a deliberate choice, not a tuned default, so left for the parent to adjust
 * themselves) and anything Oinks-denominated (Law 1 — Oinks are never money, so
 * they never move with the exchange rate).
 * ---------------------------------------------------------------------------- */

// Scales a single token reward by a rate-change ratio, keeping its £ value constant.
// Rounds up (a reward never quietly loses value on the rescale) and never drops
// below 1 token.
export function scaleRewardForRateChange(tokens, ratio) {
  if (typeof tokens !== "number" || !(tokens > 0) || !(ratio > 0)) return tokens;
  return Math.max(1, Math.ceil(tokens * ratio));
}

export function rescaleHouseholdRewards(state, ratio) {
  const scale = (v) => scaleRewardForRateChange(v, ratio);
  if (typeof state.settings.defaultHabitReward === "number") state.settings.defaultHabitReward = scale(state.settings.defaultHabitReward);
  (state.activities || []).forEach((a) => { if (typeof a.tokens === "number") { a.tokens = scale(a.tokens); if (a.seeded && typeof a.seeded === "object" && typeof a.seeded.tokens === "number") a.seeded.tokens = a.tokens; } });
  (state.profiles || []).forEach((p) => {
    if (typeof p.habitReward === "number") p.habitReward = scale(p.habitReward);
    const lc = state.learningContent && state.learningContent[p.id];
    if (!lc) return;
    // LB-283, third instance of the same omission and the worst of them: this runs when the
    // parent changes the EXCHANGE RATE, and rescales every reward so its money value holds.
    // spellingPassReward was absent, so it alone kept its old token count — silently doubling
    // or halving in real terms — while the confirmation toast promised "reward amounts
    // adjusted to match". Whatever else that toast says (see LB-267's baseline, where it
    // carries three different fallbacks), it should at least be true.
    //
    // And the record of what the app wrote has to move WITH the values, or it stops meaning
    // anything. `_seededRewards` is how the app distinguishes "we set this" from "the parent
    // set this" (see stampSeededRewards). Scaling lc[key] without scaling the record made every
    // rate-changing household look parent-edited. Activities already do this correctly one line
    // above — a.seeded.tokens is scaled alongside a.tokens — so this is the learning half
    // catching up with the half that was right. Found by Claude Code reviewing the retro repair
    // that depended on it.
    const seededRec = lc._seededRewards && typeof lc._seededRewards === "object" ? lc._seededRewards : null;
    ["spellingReward", "spellingPassReward", "vocabPassReward", "vocabFullReward", "ttStreakPassReward", "ttStreakFullReward", "mathsPassReward", "mathsFullReward"].forEach((key) => {
      if (typeof lc[key] === "number") lc[key] = scale(lc[key]);
      if (seededRec && typeof seededRec[key] === "number") seededRec[key] = scale(seededRec[key]);
    });
  });
  return state;
}

/* ------------------------------ spending cap -------------------------------
 * A per-child weekly earnings limit (in £), on by default. Enforced across the three
 * child-initiated earning surfaces (ad-hoc "Did something else" asks, earning-activity
 * pills, and Learn & Earn game rewards) — see capStatusForNewEarning below for the shared
 * three-tier decision (proceed normally / breaches, still send to parent / already blocked).
 * Redemptions, vault transfers, and parent-issued manual adjustments never count toward it.
 * ---------------------------------------------------------------------------- */

// null means "no cap in force" (either the child has no spendingCap config yet, or it's
// switched off) — callers treat null as "always proceed normally".

export function spendingCapPence(child) {
  const cap = child?.spendingCap;
  if (!cap || cap.enabled === false) return null;
  return Math.round((cap.weeklyGBP || 0) * 100);
}

// Sums this child's already-credited earnings for the current Monday-start week, in pence.
// Only transactions explicitly tagged `earnCap: true` at write time count — see addTransaction
// call sites for approved earn/habit_reward requests, approved Learn & Earn rewards, and the
// instant Learn & Earn credit path. Parent-issued ad-hoc credits, vault transfers, and
// redemption spends are never tagged this way, so they're excluded automatically.

// Sums this child's already-credited earnings for the current Monday-start week, in pence.
// Only transactions explicitly tagged `earnCap: true` at write time count — see addTransaction
// call sites for approved earn/habit_reward requests, approved Learn & Earn rewards, and the
// instant Learn & Earn credit path. Parent-issued ad-hoc credits, vault transfers, and
// redemption spends are never tagged this way, so they're excluded automatically. A credit
// that's since been undone (see reverseTransaction/reverseRequest, which flip `reversed` on
// the original row and log a separate offsetting debit) is excluded too — otherwise a parent
// reversing an over-cap approval could never actually lift the block it caused, since the
// offsetting debit row itself isn't tagged earnCap and so wouldn't cancel it out in this sum.
export function earnedThisWeekPence(transactions, childId, exchangeRate, today = todayStr()) {
  const weekStart = new Date(weekStartOf(today) + "T00:00:00");
  return (transactions || [])
    .filter((t) => t.childId === childId && t.type === "credit" && t.earnCap && !t.reversed && t.balance !== "vault" && new Date(t.timestamp) >= weekStart)
    .reduce((sum, t) => sum + tokensToPence(t.amount, exchangeRate), 0);
}

// Sums this child's currently-pending earning requests raised so far this week, in pence —
// counted alongside earnedThisWeekPence so several requests submitted back-to-back (each
// individually under the cap) can't stack up to more than the cap once a parent approves them
// all. Requests from a previous week that are somehow still pending don't count — they'll be
// judged against the fresh week's progress once acted on.

export function pendingEarnPence(requests, childId, exchangeRate, today = todayStr()) {
  const weekStart = new Date(weekStartOf(today) + "T00:00:00");
  return (requests || [])
    .filter((r) => r.childId === childId && r.status === "pending" && EARN_REQUEST_TYPES.includes(r.requestType) && new Date(r.timestamp) >= weekStart)
    .reduce((sum, r) => sum + tokensToPence(r.requestedAmount, exchangeRate), 0);
}

// The shared three-tier decision for "child wants to earn `newAmountTokens` more tokens right
// now" — used identically by ad-hoc/activity requests, Learn & Earn rewards, and the Rewards
// tab's own progress display (with newAmountTokens: 0).
//  - capped: false — no cap in force, always proceed.
//  - blocked: true — already at/over the cap before this amount is even added; the request
//    should never be created/credited.
//  - breaches: true — under the cap right now, but this amount would push over it; proceed,
//    but flag it so a parent sees a warning before approving.
//  - neither blocked nor breaches — comfortably under the cap, proceed exactly as normal.

export function capStatusForNewEarning(child, transactions, requests, exchangeRate, newAmountTokens) {
  const capPence = spendingCapPence(child);
  if (capPence === null) {
    return { capped: false, blocked: false, breaches: false, earnedPence: 0, pendingPence: 0, capPence: null, projectedPence: 0 };
  }
  const earnedPence = earnedThisWeekPence(transactions, child.id, exchangeRate);
  const pendingPence = pendingEarnPence(requests, child.id, exchangeRate);
  const usedPence = earnedPence + pendingPence;
  const newPence = tokensToPence(newAmountTokens || 0, exchangeRate);
  const blocked = usedPence >= capPence;
  const projectedPence = usedPence + newPence;
  const breaches = !blocked && projectedPence > capPence;
  return { capped: true, blocked, breaches, earnedPence, pendingPence, capPence, projectedPence };
}

// LB-355 (Alan, 21 Sep): "however a goal is entered, in tokens or in £, it is stored as a
// money amount, and the tokens outstanding are calculated from the current rate whenever they
// are shown." A goal is { targetPence }. A rate change therefore never touches a goal: it
// still costs the same money and its token target follows the rate. These two are the ONLY
// way to read a goal's price; nothing reads targetPence or the legacy targetTokens directly.
// A goal saved by a build before this (targetTokens only) is migrated once by migrateState at
// the rate in force; the targetTokens branch below is for the one read that can land between.
export function goalTargetTokens(goal, exchangeRate) {
  if (!goal) return 0;
  if (Number.isFinite(goal.targetPence)) return goal.targetPence > 0 ? Math.max(1, penceToTokens(goal.targetPence, exchangeRate)) : 0;
  return Number.isFinite(goal.targetTokens) ? goal.targetTokens : 0;
}
export function goalPricePence(goal, exchangeRate) {
  if (!goal) return 0;
  if (Number.isFinite(goal.targetPence)) return goal.targetPence;
  return tokensToPence(Number.isFinite(goal.targetTokens) ? goal.targetTokens : 0, exchangeRate);
}

// Goals are funded by tokens first, then the vault balance tops up any remainder (both can
// contribute to the same goal). Returns each balance's contribution in token-equivalent units
// so the UI can render a two-tone progress bar and a plain-language breakdown.

export function goalProgress(child, targetTokens, exchangeRate) {
  const tokenPart = Math.min(child.tokenBalance, targetTokens);
  const vaultTokens = penceToTokens(child.vaultBalancePence, exchangeRate);
  const vaultPart = Math.min(vaultTokens, Math.max(0, targetTokens - tokenPart));
  const combined = tokenPart + vaultPart;
  const pct = targetTokens > 0 ? Math.min(100, Math.round((combined / targetTokens) * 100)) : 0;
  const tokenPct = targetTokens > 0 ? Math.min(100, Math.round((tokenPart / targetTokens) * 100)) : 0;
  return { tokenPart, vaultPart, combined, pct, tokenPct, reached: combined >= targetTokens, remaining: Math.max(0, targetTokens - combined) };
}

// Picks which goal Home shows: the kid-starred "top goal" if one exists, otherwise whichever
// goal is furthest along (closest to done first) so Home never lands on an empty/irrelevant
// goal. Returns null if the child has no goals at all.

export function pickTopGoal(goals, child, exchangeRate) {
  if (!goals || goals.length === 0) return null;
  const starred = goals.find((g) => g.starred);
  if (starred) return starred;
  return goals.reduce((best, g) => {
    const gp = goalProgress(child, goalTargetTokens(g, exchangeRate), exchangeRate);
    const bestGp = goalProgress(child, goalTargetTokens(best, exchangeRate), exchangeRate);
    return gp.pct > bestGp.pct ? g : best;
  }, goals[0]);
}

// bankEnabled() and withEffectiveVault() were deleted here (Alan, Aug 2026).
//
// The Savings Vault is now a permanent part of every child's profile — it can't be switched
// off, so there is no "vault is absent" state left to guard against and no reason to zero a
// balance for display. Both were stubbed-to-true no-ops after that change; deleting them
// outright rather than leaving them in place keeps ~36 dead conditionals from surviving as
// something a future reader mistakes for live logic.
//
// The old per-child flag (hasBankAccount) survives only inside migrateState, where it now
// derives earnsVaultInterest — see that function. Anything needing "does this child earn
// interest?" reads that field, NOT a vault-enabled check.

// Whether a parent gets a genuine choice of full payment source for a redemption of
// `targetTokens` from `child` — shared by the kid-request approval flow and both parent
// direct-redeem UIs so the rule is identical everywhere:
//  - if neither balance alone covers the amount, both are needed — no real choice, auto-split.
//  - if only one balance alone covers it, that's the only real option — no choice, fixed.
//  - only when BOTH balances alone are sufficient is a genuine either/or toggle offered.
// `canAllVault` is also exposed on its own (not just folded into `toggleable`) so callers can
// offer a narrower "deselect tokens, pay from the Vault alone" option even in the in-between
// case where tokens alone fall short but the Vault alone would still cover the whole thing —
// see resolveRedeemSplit's "vault-only" override below.

export function redeemSourceOptions(child, targetTokens, exchangeRate) {
  if (!child) return { canAllTokens: false, canAllVault: false, toggleable: false };
  const canAllTokens = child.tokenBalance >= targetTokens;
  const canAllVault = penceToTokens(child.vaultBalancePence, exchangeRate) >= targetTokens;
  return { canAllTokens, canAllVault, toggleable: canAllTokens && canAllVault };
}
// Resolves the actual token/vault split for redeeming `targetTokens` from `child`.
// `chosenSource` can be:
//  - "token" | "vault" — only honoured when redeemSourceOptions says a genuine either/or
//    toggle exists (both balances alone sufficient); otherwise ignored.
//  - "vault-only" — honoured whenever the Vault alone can cover the whole amount
//    (`canAllVault`), even if tokens alone can't. Lets a parent deselect tokens from an
//    otherwise-mixed default split and pay entirely from the Vault instead. Not offered
//    the other way around (deselecting the Vault from a mixed split) because that's only
//    ever possible when tokens alone are already sufficient, which is the `canAllTokens`
//    branch below and needs no override.
// With no override (or one that doesn't apply), the balances alone decide, falling back to
// goalProgress's tokens-first-then-vault auto-split when neither balance alone covers it —
// the same rule the kid-facing progress bar uses, so what a redemption actually draws on
// always matches what was shown as "combined".

export function resolveRedeemSplit(child, targetTokens, exchangeRate, chosenSource) {
  const { canAllTokens, canAllVault, toggleable } = redeemSourceOptions(child, targetTokens, exchangeRate);
  if (toggleable) {
    return chosenSource === "vault" ? { tokenPart: 0, vaultPart: targetTokens } : { tokenPart: targetTokens, vaultPart: 0 };
  }
  if (chosenSource === "vault-only" && canAllVault) {
    return { tokenPart: 0, vaultPart: targetTokens };
  }
  if (canAllTokens) return { tokenPart: targetTokens, vaultPart: 0 };
  // Tokens alone can't cover it — always spend whatever tokens the child already has first
  // and let the Savings Vault top up the remainder, rather than jumping straight to 100%
  // vault just because the vault alone happens to be enough. This matches the tokens-first
  // rule the progress bar itself shows (see goalProgress), so "mixed" redemptions actually
  // draw on both balances instead of skipping tokens entirely. (A parent can still override
  // this default via "vault-only" above, when the Vault alone is enough.)
  const gp = goalProgress(child, targetTokens, exchangeRate);
  return { tokenPart: gp.tokenPart, vaultPart: gp.vaultPart };
}
